import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";
import { assertReportAdmission, NativeConditionError, validateConditionalDefinition } from "../nativeConditions.mjs";
import { isNativeReportsV31Payload, resolveNativeLayoutPlan, type NativeBand, type NativeElement } from "../nativeLayout.mjs";

export type DesignerValidationLevel = "error" | "warning" | "info";

export type DesignerValidationMessage = {
  level: DesignerValidationLevel;
  path: string;
  message: string;
};

type JsonRecord = Record<string, unknown>;
type NativeExpressionValidationContext = {
  columnsById?: Map<string, number>;
  currentColumnId?: string;
  currentColumnIndex?: number;
};
type NativeFieldReferenceContext = {
  columnIds: Set<string>;
  sampleRow: JsonRecord | null;
};

export function validateDesignerPayload(payload: FormattedInvoicePayload | null): DesignerValidationMessage[] {
  if (!payload) {
    return [{ level: "error", path: "$", message: "Definition JSON could not be parsed." }];
  }

  const messages: DesignerValidationMessage[] = [];
  let conditionalProfile = false;
  try {
    const admission = assertReportAdmission(payload, "validate");
    if (admission.hasTypedContent || admission.requiredFeatures.some(feature => ["typed-expressions-v1", "report-parameters-v1"].includes(feature))) {
      const conditions = validateConditionalDefinition(payload);
      return [
        ...conditions.errors.map((error): DesignerValidationMessage => ({ level: "error", path: error.path, message: error.message })),
        ...conditions.warnings.map((warning): DesignerValidationMessage => ({ level: "warning", path: warning.path, message: warning.message })),
        { level: "warning", path: "layout", message: "Static typed/parameter validation passed. Execution requires an owned internal permit; customer rendering and API persistence remain unavailable." },
      ];
    }
    conditionalProfile = admission.requiredFeatures.includes("conditional-rules-v1");
  } catch (error) {
    if (!(error instanceof NativeConditionError)) throw error;
    return [{ level: "error", path: error.path, message: error.message }];
  }
  const conditions = validateConditionalDefinition(payload);
  messages.push(...conditions.errors.map((error): DesignerValidationMessage => ({ level: "error", path: error.path, message: error.message })));
  messages.push(...conditions.warnings.map((warning): DesignerValidationMessage => ({ level: "warning", path: warning.path, message: warning.message })));
  const layout = asRecord(payload.layout);
  if (isNativeReportsV31Payload(payload)) {
    validateNativeLayout(payload, layout, messages);
    if (!messages.length) {
      messages.push({ level: "info", path: "$", message: "No validation issues found for the active native reports-v3.1 payload." });
    }
    return messages;
  }

  const sections = asArray(layout.sections);
  const styles = asRecord(layout.styles);
  const datasets = asRecord(payload.datasets);

  if (!payload.definitionId) {
    messages.push({ level: "warning", path: "definitionId", message: "Definition ID is missing." });
  }

  function validateNativeLayout(payload: FormattedInvoicePayload, layout: JsonRecord, messages: DesignerValidationMessage[]) {
    const bands = asArray(layout.bands).map(asRecord) as NativeBand[];
    if (!bands.length) {
      messages.push({ level: "error", path: "layout.bands", message: "Native reports-v3.1 layout requires at least one band." });
      return;
    }
    const bandIds = new Set<string>();
    const elementIds = new Set<string>();
    bands.forEach((band, bandIndex) => {
      const bandPath = `layout.bands[${bandIndex}]`;
      const bandId = stringValue(band.id);
      if (!bandId) messages.push({ level: "error", path: `${bandPath}.id`, message: "Band ID is required." });
      else if (bandIds.has(bandId)) messages.push({ level: "error", path: `${bandPath}.id`, message: `Duplicate band ID "${bandId}".` });
      else bandIds.add(bandId);
      if (!["reportHeader", "pageHeader", "detail", "reportFooter", "pageFooter", "groupHeader", "groupFooter"].includes(stringValue(band.role))) {
        messages.push({ level: "error", path: `${bandPath}.role`, message: `Unsupported native band role "${String(band.role || "")}".` });
      }
      const bandSizing = stringValue((band as JsonRecord).sizing || ((band as JsonRecord).flow === true ? "flow" : "fixed")) || "fixed";
      if (!["fixed", "flow"].includes(bandSizing)) {
        messages.push({ level: "error", path: `${bandPath}.sizing`, message: "Native band sizing must be fixed or flow." });
      }
      if (bandSizing === "flow") {
        const tables = asArray(band.elements).map(asRecord).filter((element) => stringValue(element.type) === "table");
        if (tables.length !== 1) {
          messages.push({ level: "error", path: `${bandPath}.elements`, message: "Flow-capable bands must contain exactly one native table." });
        }
        const tableTop = Number(tables[0]?.y ?? 0);
        const overlaps = asArray(band.elements).map(asRecord).filter((element) => stringValue(element.type) !== "table" && Number(element.y || 0) + Number(element.height || 0) > tableTop);
        if (overlaps.length) {
          messages.push({ level: "error", path: `${bandPath}.elements`, message: "Flow-capable bands only support non-overlapping positioned prefix elements before the table." });
        }
      }
      if (!isPositiveNumber(band.height)) messages.push({ level: "error", path: `${bandPath}.height`, message: "Band height must be a positive point value." });
      if (band.dataset && !datasetExists(asRecord(payload.datasets), String(band.dataset))) {
        messages.push({ level: "warning", path: `${bandPath}.dataset`, message: `Dataset "${band.dataset}" is missing from sample data.` });
      }
      validateNativeElements(asArray(band.elements), bandPath, bandId, elementIds, [], payload, messages);
    });
    if (conditionalProfile) {
      messages.push({ level: "info", path: "layout.requiredFeatures", message: "Conditional definition validation only: editing, saving and rendering are unavailable." });
      return;
    }
    try {
      const plan = resolveNativeLayoutPlan(payload);
      plan.overflow.forEach((item) => messages.push({ level: "warning", path: `layout.bands.${item.bandId}.${item.elementId}`, message: item.message }));
    } catch (error) {
      if (!(error instanceof Error) || !("code" in error) || error.code !== "UNSUPPORTED_NATIVE_WORKBOOK") throw error;
      messages.push({ level: "error", path: "layout", message: error.message });
    }
  }

  function validateNativeElements(
    elements: unknown[],
    parentPath: string,
    bandId: string,
    elementIds: Set<string>,
    ancestors: string[],
    payload: FormattedInvoicePayload,
    messages: DesignerValidationMessage[],
  ) {
      elements.forEach((elementValue, elementIndex) => {
        const element = asRecord(elementValue) as NativeElement;
        const elementPath = `${parentPath}.elements[${elementIndex}]`;
        const elementId = stringValue(element.id);
        if (!elementId) messages.push({ level: "error", path: `${elementPath}.id`, message: "Element ID is required." });
        else if (elementIds.has(elementId)) messages.push({ level: "error", path: `${elementPath}.id`, message: `Duplicate element ID "${elementId}".` });
        else elementIds.add(elementId);
        if (!["text", "field", "container", "table", "image"].includes(stringValue(element.type))) messages.push({ level: "error", path: `${elementPath}.type`, message: "Element type must be text, field, container, table, or image." });
        for (const key of ["x", "y", "width", "height"] as const) {
          if (element[key] !== undefined && (!Number.isFinite(Number(element[key])) || Number(element[key]) < 0)) {
            messages.push({ level: "error", path: `${elementPath}.${key}`, message: `${key} must be a non-negative point value.` });
          }
        }
        if (Number(element.width ?? 72) === 0 || Number(element.height ?? 14) === 0) {
          messages.push({ level: "error", path: `${elementPath}.bounds`, message: "Element width and height must be greater than zero." });
        }
        if (element.type === "field" && !stringValue(element.binding)) {
          messages.push({ level: "error", path: `${elementPath}.binding`, message: "Field element requires a binding." });
        }
        if (element.type === "image") {
          const assetKey = stringValue((element as JsonRecord).asset);
          if (!assetKey) messages.push({ level: "error", path: `${elementPath}.asset`, message: "Image element requires an asset binding." });
          else if (!asRecord(payload.assets)[assetKey]) messages.push({ level: "warning", path: `${elementPath}.asset`, message: `Image asset "${assetKey}" is missing from report assets.` });
        }
        if (element.type === "table") {
          validateNativeTable(element, elementPath, ancestors, payload, messages);
        }
        if (element.type === "container") {
          if (ancestors.includes(elementId)) {
            messages.push({ level: "error", path: `${elementPath}.elements`, message: `Container "${elementId}" creates a cyclic native layout relationship.` });
          }

          if (ancestors.length >= 6) {
            messages.push({ level: "warning", path: `${elementPath}.elements`, message: "Native container nesting deeper than 6 levels is not supported." });
          }
          const layout = asRecord(element.layout);
          const mode = stringValue(layout.mode || "absolute");
          if (!["absolute", "vertical", "columns"].includes(mode)) {
            messages.push({ level: "error", path: `${elementPath}.layout.mode`, message: "Container layout mode must be absolute, vertical, or columns." });
          }
          validateNativeElements(asArray(element.elements), elementPath, bandId, elementIds, [...ancestors, elementId], payload, messages);
        }
      });
  }

  function validateNativeTable(
    element: NativeElement,
    elementPath: string,
    ancestors: string[],
    payload: FormattedInvoicePayload,
    messages: DesignerValidationMessage[],
  ) {
    if (ancestors.length > 0) {
      messages.push({ level: "error", path: elementPath, message: `Table "${stringValue(element.id)}" cannot be nested inside containers for paginated native rendering in 5B.1.` });
    }
    const datasetName = stringValue((element as JsonRecord).dataset);
    const dataset = datasetName ? getDataset(asRecord(payload.datasets), datasetName) : undefined;
    const rows = getRows(dataset);
    const sampleRow = firstRecord(rows);
    if (!datasetName) {
      messages.push({ level: "error", path: `${elementPath}.dataset`, message: "Native table requires a dataset binding." });
    } else if (!datasetExists(asRecord(payload.datasets), datasetName)) {
      messages.push({ level: "error", path: `${elementPath}.dataset`, message: `Dataset "${datasetName}" is missing from sample data.` });
    }
    const columns = asArray((element as JsonRecord).columns).map(asRecord);
    const calculatedColumnIds = new Map<string, number>();
    columns.forEach((column, index) => {
      const columnId = stringValue(column.id || column.key || column.binding);
      const expression = asRecord(column.expr || column.calculation);
      if (columnId && Object.keys(expression).length && !calculatedColumnIds.has(columnId)) calculatedColumnIds.set(columnId, index);
    });
    if (!columns.length) {
      messages.push({ level: "error", path: `${elementPath}.columns`, message: "Native table requires at least one column." });
    }
    const columnIds = new Set<string>();
    const columnsById = new Map<string, JsonRecord>();
    const visibleColumnIds = new Set<string>();
    let fixedWidthTotal = 0;
    columns.forEach((column, columnIndex) => {
      const columnPath = `${elementPath}.columns[${columnIndex}]`;
      const columnId = stringValue(column.id || column.key || column.binding);
      if (!columnId) messages.push({ level: "error", path: `${columnPath}.id`, message: "Native table column requires a stable ID." });
      else if (columnIds.has(columnId)) messages.push({ level: "error", path: `${columnPath}.id`, message: `Duplicate native table column ID "${columnId}".` });
      else {
        columnIds.add(columnId);
        columnsById.set(columnId, column);
        if (column.visible !== false && column.hidden !== true) visibleColumnIds.add(columnId);
      }
      const binding = stringValue(column.binding || column.key);
      const expression = asRecord(column.expr || column.calculation);
      if (!binding && !Object.keys(expression).length) messages.push({ level: "error", path: `${columnPath}.binding`, message: "Native table column requires a field binding or calculated expression." });
      else if (binding && sampleRow && !hasPath(sampleRow, binding)) messages.push({ level: "warning", path: `${columnPath}.binding`, message: `Sample row does not include "${binding}".` });
      if (Object.keys(expression).length) validateNativeExpression(expression, `${columnPath}.expr`, sampleRow, messages, { columnsById: calculatedColumnIds, currentColumnId: columnId, currentColumnIndex: columnIndex });
      const format = stringValue(column.format);
      if (format && !["money", "number", "date", "text", "string"].includes(format)) {
        messages.push({ level: "warning", path: `${columnPath}.format`, message: `Native table format "${format}" is not supported in 5B.1.` });
      }
      const width = column.width;
      if (typeof width === "number" && column.visible !== false && column.hidden !== true) fixedWidthTotal += width;
      if (width !== undefined && typeof width === "number" && width <= 0) {
        messages.push({ level: "error", path: `${columnPath}.width`, message: "Native table column width must be greater than zero." });
      }
    });
    if (fixedWidthTotal > Number(element.width || 0)) {
      messages.push({ level: "error", path: `${elementPath}.columns`, message: "Fixed native table column widths exceed the table width." });
    }
    if ((element as JsonRecord).sort !== undefined && !Array.isArray((element as JsonRecord).sort)) {
      messages.push({ level: "error", path: `${elementPath}.sort`, message: "Native table sort must be an ordered array of descriptors." });
    }
    const sortDescriptors = asArray((element as JsonRecord).sort).map(asRecord);
    const sortedFields = new Set<string>();
    sortDescriptors.forEach((sort, sortIndex) => {
      const sortPath = `${elementPath}.sort[${sortIndex}]`;
      const field = stringValue(sort.field);
      if (!field) {
        messages.push({ level: "error", path: `${sortPath}.field`, message: "Native table sort descriptor requires a field." });
      } else {
        if (sortedFields.has(field)) {
          messages.push({ level: "error", path: `${sortPath}.field`, message: `Native table sort field "${field}" is duplicated.` });
        }
        sortedFields.add(field);
        if (!columnIds.has(field) && sampleRow && !hasPath(sampleRow, field)) {
          messages.push({ level: "error", path: `${sortPath}.field`, message: `Native table sort references missing field "${field}".` });
        }
      }
      const direction = stringValue(sort.direction || "asc");
      if (!["asc", "desc", "ascending", "descending"].includes(direction)) {
        messages.push({ level: "error", path: `${sortPath}.direction`, message: "Native table sort direction must be asc or desc." });
      }
      const type = stringValue(sort.type || inferNativeSortType(field, columnsById.get(field), sampleRow));
      if (type && !["auto", "date", "number", "string"].includes(type)) {
        messages.push({ level: "error", path: `${sortPath}.type`, message: "Native table sort type must be auto, date, number, or string." });
      }
      const nulls = stringValue(sort.nulls || "last");
      if (!["first", "last"].includes(nulls)) {
        messages.push({ level: "error", path: `${sortPath}.nulls`, message: "Native table sort null policy must be first or last." });
      }
      validateNativeSortSampleValue(sort, field, columnsById.get(field), sampleRow, sortPath, messages);
    });
    const fieldReferenceContext = { columnIds, sampleRow };
    const tableFooter = asRecord((element as JsonRecord).footer);
    validateNativeFooterGeometry(tableFooter, `${elementPath}.footer`, "report", columnIds, visibleColumnIds, fieldReferenceContext, messages);
    const groups = asArray((element as JsonRecord).groups).map(asRecord);
    if (groups.length > 1) {
      messages.push({ level: "error", path: `${elementPath}.groups`, message: "Native reports-v3.1 6A supports one grouping level per table." });
    }
    groups.forEach((group, groupIndex) => {
      const groupPath = `${elementPath}.groups[${groupIndex}]`;
      const groupId = stringValue(group.id);
      if (!groupId) messages.push({ level: "error", path: `${groupPath}.id`, message: "Native table group requires a stable ID." });
      const key = asRecord(group.key);
      const field = stringValue(group.field || key.field);
      const expr = asRecord(group.expr || key.expr || group.expression || key.expression);
      if (!field && !Object.keys(expr).length) {
        messages.push({ level: "error", path: `${groupPath}.key`, message: "Native table group requires a field or calculated expression key." });
      } else if (field && sampleRow && !hasPath(sampleRow, field)) {
        messages.push({ level: "warning", path: `${groupPath}.field`, message: `Sample row does not include grouping field "${field}".` });
      }
      if (Object.keys(expr).length) validateNativeExpression(expr, `${groupPath}.expr`, sampleRow, messages);
      const sort = stringValue(group.sort || "asc");
      if (!["asc", "desc", "ascending", "descending"].includes(sort)) {
        messages.push({ level: "error", path: `${groupPath}.sort`, message: "Native table group sort must be asc or desc." });
      }
      validateNonNegativePoint(group.spacingBefore, `${groupPath}.spacingBefore`, messages);
      validateNonNegativePoint(group.spacingAfter, `${groupPath}.spacingAfter`, messages);
      const pagination = asRecord(group.pagination);
      validateNonNegativePoint(pagination.spacingBefore, `${groupPath}.pagination.spacingBefore`, messages);
      validateNonNegativePoint(pagination.spacingAfter, `${groupPath}.pagination.spacingAfter`, messages);
      validateNativeFooterGeometry(asRecord(group.footer), `${groupPath}.footer`, "group", columnIds, visibleColumnIds, fieldReferenceContext, messages);
    });
  }

  function validateNativeFooterGeometry(
    footer: JsonRecord,
    path: string,
    defaultScope: "group" | "report",
    columnIds: Set<string>,
    visibleColumnIds: Set<string>,
    fieldReferenceContext: NativeFieldReferenceContext,
    messages: DesignerValidationMessage[],
  ) {
    if (footer.height !== undefined && (!Number.isFinite(Number(footer.height)) || Number(footer.height) < 0)) {
      messages.push({ level: "error", path: `${path}.height`, message: "Summary row height must be a non-negative point value." });
    }
    const cells = asArray(footer.cells).map(asRecord);
    cells.forEach((cell, index) => {
      const cellPath = `${path}.cells[${index}]`;
      const columnId = stringValue(cell.columnId);
      if (columnId && !columnIds.has(columnId)) {
        messages.push({ level: "error", path: `${cellPath}.columnId`, message: `Summary cell references unknown column "${columnId}".` });
      } else if (columnId && !visibleColumnIds.has(columnId)) {
        messages.push({ level: "warning", path: `${cellPath}.columnId`, message: `Summary cell targets hidden column "${columnId}". Choose a visible output column or show the column.` });
      }
      if (cell.colSpan !== undefined && (!Number.isFinite(Number(cell.colSpan)) || Number(cell.colSpan) <= 0)) {
        messages.push({ level: "error", path: `${cellPath}.colSpan`, message: "Summary cell colSpan must be greater than zero." });
      }
      const format = stringValue(cell.format);
      if (format && !["money", "number", "date", "text", "string"].includes(format)) {
        messages.push({ level: "warning", path: `${cellPath}.format`, message: `Summary cell format "${format}" is not supported by native Preview/PDF rendering.` });
      }
      validateNativeAggregate(cell.aggregate, `${cellPath}.aggregate`, fieldReferenceContext, defaultScope, messages);
    });
  }

  if (!sections.length) {
    messages.push({ level: "error", path: "layout.sections", message: "At least one layout section is required." });
  }

  sections.forEach((sectionValue, sectionIndex) => {
    const section = asRecord(sectionValue);
    const sectionPath = `layout.sections[${sectionIndex}]`;
    const type = stringValue(section.type);
    const id = stringValue(section.id) || `section-${sectionIndex + 1}`;
    const datasetName = stringValue(section.dataset);
    const styleName = stringValue(section.style);

    if (!type) {
      messages.push({ level: "error", path: `${sectionPath}.type`, message: `Section ${id} is missing a type.` });
    }

    if (styleName && !styles[styleName]) {
      messages.push({ level: "warning", path: `${sectionPath}.style`, message: `Style "${styleName}" is not defined.` });
    }

    if (datasetName && !datasetExists(datasets, datasetName)) {
      messages.push({ level: "error", path: `${sectionPath}.dataset`, message: `Dataset "${datasetName}" is missing from sample data.` });
    }

    if (type === "keyValueGrid") {
      validateKeyValueGrid(section, datasets, datasetName, sectionPath, messages);
    }

    if (type === "table") {
      validateTable(section, datasets, datasetName, sectionPath, messages);
    }

    validateOutputs(section, sectionPath, messages);
    validateExcelPlacement(section, sectionPath, messages);
  });

  if (!messages.length) {
    messages.push({ level: "info", path: "$", message: "No validation issues found for the active sample payload." });
  }

  return messages;
}

export function parseDesignerJson(text: string): { payload: FormattedInvoicePayload | null; error: string } {
  try {
    const parsed = JSON.parse(text) as FormattedInvoicePayload;
    assertReportAdmission(parsed, "import");
    return { payload: parsed, error: "" };
  } catch (error) {
    return { payload: null, error: error instanceof Error ? error.message : "Invalid JSON." };
  }
}

export function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function validateKeyValueGrid(
  section: JsonRecord,
  datasets: JsonRecord,
  datasetName: string,
  sectionPath: string,
  messages: DesignerValidationMessage[],
) {
  const fields = asArray(section.fields);
  const dataset = getDataset(datasets, datasetName);
  const sampleRecord = firstRecord(dataset);
  const columns = Number(asRecord(getStyleLike(section)).columns ?? 0);

  if (!fields.length) {
    messages.push({ level: "warning", path: `${sectionPath}.fields`, message: "Key-value grid has no fields." });
  }

  fields.forEach((fieldValue, fieldIndex) => {
    const field = asRecord(fieldValue);
    const fieldPath = `${sectionPath}.fields[${fieldIndex}]`;
    const key = stringValue(field.key);
    if (!key) {
      messages.push({ level: "error", path: `${fieldPath}.key`, message: "Key-value field is missing a key." });
    } else if (sampleRecord && !hasPath(sampleRecord, key)) {
      messages.push({ level: "warning", path: `${fieldPath}.key`, message: `Sample dataset does not include "${key}".` });
    }

    const column = Number(field.column);
    if (field.column !== undefined && (!Number.isInteger(column) || column < 1)) {
      messages.push({ level: "error", path: `${fieldPath}.column`, message: "Explicit column must be an integer >= 1." });
    }
    if (Number.isInteger(column) && columns > 0 && column > columns) {
      messages.push({ level: "warning", path: `${fieldPath}.column`, message: `Column ${column} exceeds section columns (${columns}).` });
    }
  });
}

function validateTable(
  section: JsonRecord,
  datasets: JsonRecord,
  datasetName: string,
  sectionPath: string,
  messages: DesignerValidationMessage[],
) {
  const columns = asArray(section.columns);
  const rows = getRows(getDataset(datasets, datasetName));
  const sampleRow = firstRecord(rows);

  if (!columns.length) {
    messages.push({ level: "warning", path: `${sectionPath}.columns`, message: "Table has no columns." });
  }

  columns.forEach((columnValue, columnIndex) => {
    const column = asRecord(columnValue);
    const columnPath = `${sectionPath}.columns[${columnIndex}]`;
    const key = stringValue(column.key);
    if (!key) {
      messages.push({ level: "error", path: `${columnPath}.key`, message: "Table column is missing a key." });
    } else if (sampleRow && !hasPath(sampleRow, key)) {
      messages.push({ level: "warning", path: `${columnPath}.key`, message: `Sample row does not include "${key}".` });
    }
  });
}

function validateOutputs(section: JsonRecord, sectionPath: string, messages: DesignerValidationMessage[]) {
  const outputs = asArray(section.outputs);
  const allowed = new Set(["preview", "pdf", "excel"]);
  outputs.forEach((output, index) => {
    if (!allowed.has(String(output))) {
      messages.push({ level: "error", path: `${sectionPath}.outputs[${index}]`, message: `Unsupported output "${String(output)}".` });
    }
  });
}

function validateExcelPlacement(section: JsonRecord, sectionPath: string, messages: DesignerValidationMessage[]) {
  const excel = asRecord(asRecord(section.placement).excel);
  Object.entries(excel).forEach(([key, value]) => {
    if ((key.toLowerCase().includes("cell") || key.toLowerCase().includes("range")) && typeof value === "string" && !isCellOrRange(value)) {
      messages.push({ level: "warning", path: `${sectionPath}.placement.excel.${key}`, message: `"${value}" does not look like an Excel cell/range.` });
    }
  });
}

function validateNativeAggregate(
  value: unknown,
  path: string,
  fieldReferenceContext: NativeFieldReferenceContext,
  defaultScope: "group" | "report",
  messages: DesignerValidationMessage[],
) {
  if (!value) return;
  const supported = new Set(["sum", "count", "min", "max", "avg"]);
  const aggregate = typeof value === "string" ? { fn: value, scope: defaultScope } : asRecord(value);
  const fn = stringValue(aggregate.fn || aggregate.function);
  if (!supported.has(fn)) {
    messages.push({ level: "error", path, message: `Unsupported native table aggregate "${fn}". Supported functions are sum, count, min, max, and avg.` });
  }
  const scope = stringValue(aggregate.scope || defaultScope);
  if (!["group", "report"].includes(scope)) {
    messages.push({ level: "error", path: `${path}.scope`, message: "Native table aggregate scope must be group or report." });
  }
  const field = stringValue(aggregate.field);
  if (field && !fieldReferenceExists(field, fieldReferenceContext)) {
    messages.push({ level: "error", path: `${path}.field`, message: `Native table aggregate source references missing field or calculated column "${field}".` });
  }
  const expr = asRecord(aggregate.expr || aggregate.expression);
  if (Object.keys(expr).length) validateNativeExpression(expr, `${path}.expr`, fieldReferenceContext.sampleRow, messages);
}

function validateNativeExpression(
  expression: JsonRecord,
  path: string,
  sampleRow: JsonRecord | null,
  messages: DesignerValidationMessage[],
  context: NativeExpressionValidationContext = {},
) {
  if (Object.prototype.hasOwnProperty.call(expression, "const")) {
    const value = expression.const;
    if (value !== null && value !== undefined && value !== "" && !Number.isFinite(Number(value))) {
      messages.push({ level: "error", path: `${path}.const`, message: "Native calculated expression constants must be finite numeric values." });
    }
    return;
  }
  const field = stringValue(expression.field);
  if (field) {
    const referencedColumnIndex = context.columnsById?.get(field);
    if (referencedColumnIndex !== undefined) {
      if (field === context.currentColumnId) {
        messages.push({ level: "error", path: `${path}.field`, message: `Calculated field "${field}" cannot reference itself.` });
      } else if (context.currentColumnIndex !== undefined && referencedColumnIndex >= context.currentColumnIndex) {
        messages.push({ level: "error", path: `${path}.field`, message: `Calculated field "${field}" must be defined before this expression or removed to avoid circular/forward references.` });
      }
      return;
    }
    const sampleValue = sampleRow ? getPath(sampleRow, field) : undefined;
    if (sampleRow && !hasPath(sampleRow, field)) {
      messages.push({ level: "warning", path: `${path}.field`, message: `Sample row does not include calculated field "${field}".` });
    } else if (sampleValue !== null && sampleValue !== undefined && sampleValue !== "" && !Number.isFinite(Number(sampleValue))) {
      messages.push({ level: "error", path: `${path}.field`, message: `Calculated field operand "${field}" is not numeric in the sample row.` });
    }
    return;
  }
  if (expression.aggregate) {
    validateNativeAggregate(expression.aggregate, `${path}.aggregate`, { columnIds: new Set(), sampleRow }, "report", messages);
    return;
  }
  const op = stringValue(expression.op);
  if (!["add", "sub", "mul", "div"].includes(op)) {
    messages.push({ level: "error", path: `${path}.op`, message: "Native calculated expression op must be add, sub, mul, or div." });
    return;
  }
  const args = asArray(expression.args).length ? asArray(expression.args) : [expression.left, expression.right].filter(Boolean);
  if (args.length < 2 && op !== "sub") {
    messages.push({ level: "error", path, message: `Native calculated expression "${op}" requires at least two operands.` });
  }
  args.forEach((arg, index) => validateNativeExpression(asRecord(arg), `${path}.args[${index}]`, sampleRow, messages, context));
  if (op === "div") {
    const denominator = asRecord(args[1]);
    if (Object.prototype.hasOwnProperty.call(denominator, "const") && Number(denominator.const) === 0) {
      messages.push({ level: "error", path: `${path}.right`, message: "Native calculated expression division by zero is not supported." });
    }
  }
}

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? value as JsonRecord : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function isPositiveNumber(value: unknown): boolean {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
}

function validateNonNegativePoint(value: unknown, path: string, messages: DesignerValidationMessage[]) {
  if (value === undefined) return;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) {
    messages.push({ level: "error", path, message: "Native group spacing must be a non-negative point value." });
  }
}

function inferNativeSortType(field: string, column: JsonRecord | undefined, sampleRow: JsonRecord | null): string {
  const format = stringValue(column?.format);
  if (["date", "number", "money"].includes(format)) return format === "money" ? "number" : format;
  const value = field && sampleRow ? getPath(sampleRow, field) : undefined;
  if (value instanceof Date) return "date";
  if (typeof value === "number") return "number";
  if (typeof value === "string" && parseNativeSortDate(value) !== null) return "date";
  return "auto";
}

function validateNativeSortSampleValue(sort: JsonRecord, field: string, column: JsonRecord | undefined, sampleRow: JsonRecord | null, path: string, messages: DesignerValidationMessage[]) {
  const configuredType = stringValue(sort.type);
  const type = configuredType || inferNativeSortType(field, column, sampleRow);
  if (!field || !sampleRow || !["date", "number"].includes(type)) return;
  const columnExpression = asRecord(column?.expr || column?.calculation);
  const value = column && stringValue(column.id) === field && Object.keys(columnExpression).length ? undefined : getPath(sampleRow, field);
  if (value === null || value === undefined || value === "") return;
  if (type === "date" && parseNativeSortDate(value) === null) {
    messages.push({ level: "error", path: `${path}.type`, message: `Native table sort field "${field}" does not contain supported date values in sample data.` });
  }
  if (type === "number" && !Number.isFinite(Number(value))) {
    messages.push({ level: "error", path: `${path}.type`, message: `Native table sort field "${field}" does not contain numeric values in sample data.` });
  }
}

function parseNativeSortDate(value: unknown): number | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.getTime();
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function getDataset(datasets: JsonRecord, name: string): unknown {
  if (!name) return undefined;
  return name.split(".").reduce<unknown>((current, key) => asRecord(current)[key], datasets);
}

function datasetExists(datasets: JsonRecord, name: string): boolean {
  return getDataset(datasets, name) !== undefined;
}

function getRows(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  for (const key of ["rows", "Rows", "items", "Items", "entries", "Entries"]) {
    const rows = record[key];
    if (Array.isArray(rows)) return rows;
  }
  return [];
}

function firstRecord(value: unknown): JsonRecord | null {
  const rows = Array.isArray(value) ? value : getRows(value);
  if (rows.length) return asRecord(rows.find((row) => Object.keys(asRecord(row)).length > 0));
  const record = asRecord(value);
  return Object.keys(record).length ? record : null;
}

function hasPath(record: JsonRecord, path: string): boolean {
  return getPath(record, path) !== undefined;
}

function fieldReferenceExists(field: string, context: NativeFieldReferenceContext): boolean {
  return context.columnIds.has(field) || Boolean(context.sampleRow && hasPath(context.sampleRow, field));
}

function getPath(record: JsonRecord, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => asRecord(current)[key], record);
}

function isCellOrRange(value: string): boolean {
  return /^[A-Z]+[1-9][0-9]*(?::[A-Z]+[1-9][0-9]*)?$/i.test(value.trim());
}

function getStyleLike(section: JsonRecord): JsonRecord {
  return section;
}
